(() => {
  'use strict';

  const init = () => {
    const productPage = document.querySelector('[data-ric-product-section]');
    if (!productPage || productPage.dataset.ricProductInitialized === 'true') return;
    productPage.dataset.ricProductInitialized = 'true';

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    /* --------------------------------------------------------
       RIC VARIANT COLOR / NAME OPTIONS
       
       Converts the old image-only color swatches into clear,
       readable variant cards.

       Examples:
       BLACK 5001
       BLACK 5002
       BLACK 5003
       RED - 1001
       SILVER - 1002

       Multiple variants sharing the same color family receive
       different subtle background treatments so they remain
       visually distinguishable.
       -------------------------------------------------------- */
    const enhanceVariantColorOptions = () => {
      const colorLabels = Array.from(
        productPage.querySelectorAll(
          '.ric-variant-group label[data-color]'
        )
      );

      if (!colorLabels.length) return;

      const normalize = (value) =>
        String(value || '')
          .trim()
          .toLowerCase()
          .replace(/[_]+/g, ' ')
          .replace(/\s+/g, ' ');

      const colorFamilies = {
        black: {
          base: '#111111',
          text: '#ffffff'
        },

        red: {
          base: '#d10a0a',
          text: '#ffffff'
        },

        silver: {
          base: '#aeb4ba',
          text: '#101010'
        },

        grey: {
          base: '#666666',
          text: '#ffffff'
        },

        gray: {
          base: '#666666',
          text: '#ffffff'
        },

        white: {
          base: '#f2f2f2',
          text: '#111111'
        },

        blue: {
          base: '#123f78',
          text: '#ffffff'
        },

        navy: {
          base: '#0b1d3a',
          text: '#ffffff'
        },

        green: {
          base: '#17633b',
          text: '#ffffff'
        },

        yellow: {
          base: '#d3a900',
          text: '#111111'
        },

        gold: {
          base: '#b58a24',
          text: '#111111'
        },

        orange: {
          base: '#c95b13',
          text: '#ffffff'
        },

        purple: {
          base: '#6c3a8f',
          text: '#ffffff'
        },

        violet: {
          base: '#7542a8',
          text: '#ffffff'
        },

        pink: {
          base: '#b94f7c',
          text: '#ffffff'
        },

        brown: {
          base: '#65452f',
          text: '#ffffff'
        },

        beige: {
          base: '#b8a27d',
          text: '#111111'
        }
      };

      const findFamily = (value) => {
        const normalized = normalize(value);

        const exact = Object.keys(colorFamilies).find(
          (family) => normalized === family
        );

        if (exact) {
          return exact;
        }

        return (
          Object.keys(colorFamilies).find(
            (family) =>
              normalized.includes(family)
          ) || null
        );
      };

      const makeBackground = (
        family,
        occurrence
      ) => {
        const info =
          colorFamilies[family] || {
            base: '#252525',
            text: '#ffffff'
          };

        /*
         * BLACK
         *
         * Black 5001 / 5002 / 5003 receive different subtle
         * technical finishes.
         */
        if (family === 'black') {
          const patterns = [
            'linear-gradient(135deg, #080808 0%, #1c1c1c 48%, #080808 100%)',

            'radial-gradient(circle at 25% 20%, #303030 0%, #151515 34%, #070707 100%)',

            'linear-gradient(90deg, #050505 0%, #242424 48%, #090909 100%)',

            'repeating-linear-gradient(135deg, #0a0a0a 0, #0a0a0a 7px, #171717 7px, #171717 10px)'
          ];

          return patterns[
            occurrence % patterns.length
          ];
        }

        /*
         * SILVER
         *
         * Metallic treatment.
         */
        if (family === 'silver') {
          const patterns = [
            'linear-gradient(135deg, #7d8389 0%, #e3e5e7 48%, #92989d 100%)',

            'linear-gradient(90deg, #8c9298 0%, #f0f1f2 50%, #777d83 100%)',

            'radial-gradient(circle at 30% 20%, #f4f5f6 0%, #b6bcc1 48%, #73797f 100%)'
          ];

          return patterns[
            occurrence % patterns.length
          ];
        }

        /*
         * RED
         */
        if (family === 'red') {
          const patterns = [
            'linear-gradient(135deg, #8e0000 0%, #fa0505 52%, #a40000 100%)',

            'linear-gradient(90deg, #b00000 0%, #fa1717 50%, #860000 100%)',

            'radial-gradient(circle at 25% 20%, #ff3b3b 0%, #d30606 45%, #780000 100%)'
          ];

          return patterns[
            occurrence % patterns.length
          ];
        }

        /*
         * BLUE
         */
        if (family === 'blue') {
          return occurrence % 2 === 0
            ? 'linear-gradient(135deg, #0b2c57 0%, #1f67ae 52%, #092447 100%)'
            : 'linear-gradient(90deg, #092244 0%, #174f8b 50%, #071b35 100%)';
        }

        /*
         * VIOLET / PURPLE
         */
        if (
          family === 'violet' ||
          family === 'purple'
        ) {
          return occurrence % 2 === 0
            ? 'linear-gradient(135deg, #422064 0%, #8245aa 52%, #35194f 100%)'
            : 'linear-gradient(90deg, #542778 0%, #9256ba 50%, #3b1c58 100%)';
        }

        /*
         * WHITE
         */
        if (family === 'white') {
          return 'linear-gradient(135deg, #d7d7d7 0%, #ffffff 50%, #bdbdbd 100%)';
        }

        return info.base;
      };

      const occurrences = {};

      colorLabels.forEach((label) => {
        const value =
          label.getAttribute('data-color') ||
          label.querySelector('.tool-tip')?.textContent ||
          label.textContent ||
          '';

        const cleanValue =
          String(value).trim();

        if (!cleanValue) {
          return;
        }

        const family =
          findFamily(cleanValue);

        const familyKey =
          family || normalize(cleanValue);

        const occurrence =
          occurrences[familyKey] || 0;

        occurrences[familyKey] =
          occurrence + 1;

        const info =
          family
            ? colorFamilies[family]
            : {
                base: '#222222',
                text: '#ffffff'
              };

        /*
         * Make the actual variant name visible.
         */
        label.textContent =
          cleanValue;

        label.setAttribute(
          'aria-label',
          `Select ${cleanValue}`
        );

        label.setAttribute(
          'title',
          cleanValue
        );

        label.classList.add(
          'ric-variant-color-card'
        );

        /*
         * Responsive dimensions.
         */
        label.style.setProperty(
          'width',
          'auto',
          'important'
        );

        label.style.setProperty(
          'min-width',
          '108px',
          'important'
        );

        label.style.setProperty(
          'max-width',
          '180px',
          'important'
        );

        label.style.setProperty(
          'height',
          '44px',
          'important'
        );

        /*
         * Spacing.
         */
        label.style.setProperty(
          'padding',
          '8px 13px',
          'important'
        );

        /*
         * Layout.
         */
        label.style.setProperty(
          'display',
          'inline-flex',
          'important'
        );

        label.style.setProperty(
          'align-items',
          'center',
          'important'
        );

        label.style.setProperty(
          'justify-content',
          'center',
          'important'
        );

        label.style.setProperty(
          'box-sizing',
          'border-box',
          'important'
        );

        /*
         * RIC industrial shape.
         */
        label.style.setProperty(
          'border-radius',
          '3px',
          'important'
        );

        label.style.setProperty(
          'border',
          '1px solid rgba(255,255,255,.20)',
          'important'
        );

        /*
         * Color surface.
         */
        label.style.setProperty(
          'background-image',
          makeBackground(
            family,
            occurrence
          ),
          'important'
        );

        label.style.setProperty(
          'background-color',
          info.base,
          'important'
        );

        label.style.setProperty(
          'background-size',
          'cover',
          'important'
        );

        label.style.setProperty(
          'background-position',
          'center',
          'important'
        );

        /*
         * Typography.
         */
        label.style.setProperty(
          'color',
          info.text,
          'important'
        );

        label.style.setProperty(
          'font-size',
          '10px',
          'important'
        );

        label.style.setProperty(
          'font-weight',
          '800',
          'important'
        );

        label.style.setProperty(
          'letter-spacing',
          '.04em',
          'important'
        );

        label.style.setProperty(
          'line-height',
          '1.15',
          'important'
        );

        label.style.setProperty(
          'text-align',
          'center',
          'important'
        );

        label.style.setProperty(
          'white-space',
          'normal',
          'important'
        );

        label.style.setProperty(
          'word-break',
          'normal',
          'important'
        );

        label.style.setProperty(
          'text-shadow',
          info.text === '#ffffff'
            ? '0 1px 2px rgba(0,0,0,.55)'
            : 'none',
          'important'
        );

        /*
         * Smooth interaction.
         */
        label.style.setProperty(
          'transition',
          'transform .2s ease, border-color .2s ease, box-shadow .2s ease, filter .2s ease',
          'important'
        );

        /*
         * Remove the old image-swatch tooltip because the
         * actual variant name is now visible.
         */
        const tooltip =
          label.querySelector(
            '.tool-tip'
          );

        if (tooltip) {
          tooltip.remove();
        }
      });
    };

    /*
     * Run immediately.
     */
    enhanceVariantColorOptions();

    /* --------------------------------------------------------
       SCROLL REVEAL
       -------------------------------------------------------- */
    const revealTargets = Array.from(
      productPage.querySelectorAll(
        '.ric-product-info > *, .ric-gallery__topbar, .ric-gallery__navigation, .ric-product-trust__item'
      )
    );

    revealTargets.forEach(
      (element, index) => {
        element.setAttribute(
          'data-ric-reveal',
          ''
        );

        element.style.transitionDelay =
          `${Math.min(index * 45, 360)}ms`;
      }
    );

    if (
      'IntersectionObserver' in window &&
      !reduceMotion
    ) {
      const observer =
        new IntersectionObserver(
          (entries, obs) => {
            entries.forEach(
              (entry) => {
                if (
                  !entry.isIntersecting
                ) {
                  return;
                }

                entry.target.classList.add(
                  'is-visible'
                );

                obs.unobserve(
                  entry.target
                );
              }
            );
          },
          {
            threshold: 0.08,
            rootMargin: '0px 0px -20px'
          }
        );

      revealTargets.forEach(
        (element) =>
          observer.observe(element)
      );
    } else {
      revealTargets.forEach(
        (element) =>
          element.classList.add(
            'is-visible'
          )
      );
    }

    /* --------------------------------------------------------
       GALLERY
       -------------------------------------------------------- */
    const mainGallery =
      productPage.querySelector(
        '[data-ric-gallery-main]'
      );

    const thumbsContainer =
      productPage.querySelector(
        '[data-ric-gallery-thumbs]'
      );

    const thumbnails =
      Array.from(
        productPage.querySelectorAll(
          '[data-ric-gallery-thumbs] .ric-gallery__thumb'
        )
      );

    const currentCounter =
      productPage.querySelector(
        '.ric-gallery__current'
      );

    const totalCounter =
      productPage.querySelector(
        '.ric-gallery__total'
      );

    const previousButton =
      productPage.querySelector(
        '[data-ric-gallery-prev]'
      );

    const nextButton =
      productPage.querySelector(
        '[data-ric-gallery-next]'
      );

    let activeIndex =
      Math.max(
        0,
        thumbnails.findIndex(
          (thumb) => {
            return (
              thumb.dataset.mediaId ===
              mainGallery
                ?.querySelector(
                  '[data-media-id]'
                )
                ?.dataset.mediaId
            );
          }
        )
      );

    if (
      activeIndex < 0
    ) {
      activeIndex = 0;
    }

    if (totalCounter) {
      totalCounter.textContent =
        String(
          thumbnails.length
        ).padStart(
          2,
          '0'
        );
    }

    const getMediaId =
      (element) => {
        return (
          element?.getAttribute(
            'data-media-id'
          ) || ''
        );
      };

    const scrollThumbIntoView =
      (thumbnail) => {
        if (
          !thumbnail ||
          !thumbsContainer ||
          window.innerWidth > 767
        ) {
          return;
        }

        thumbnail.scrollIntoView({
          behavior:
            reduceMotion
              ? 'auto'
              : 'smooth',
          block: 'nearest',
          inline: 'center'
        });
      };

    const animateGallery =
      () => {
        if (
          !mainGallery ||
          reduceMotion
        ) {
          return;
        }

        mainGallery.classList.remove(
          'ric-gallery-changing'
        );

        void mainGallery.offsetWidth;

        mainGallery.classList.add(
          'ric-gallery-changing'
        );

        window.setTimeout(
          () => {
            mainGallery.classList.remove(
              'ric-gallery-changing'
            );
          },
          300
        );
      };

    const setActiveState =
      (
        index,
        scroll = true
      ) => {
        if (
          !thumbnails.length
        ) {
          return;
        }

        activeIndex =
          Math.max(
            0,
            Math.min(
              index,
              thumbnails.length - 1
            )
          );

        thumbnails.forEach(
          (
            thumbnail,
            i
          ) => {
            const active =
              i ===
              activeIndex;

            thumbnail.classList.toggle(
              'is-active',
              active
            );

            thumbnail.setAttribute(
              'aria-current',
              active
                ? 'true'
                : 'false'
            );
          }
        );

        if (
          currentCounter
        ) {
          currentCounter.textContent =
            String(
              activeIndex + 1
            ).padStart(
              2,
              '0'
            );
        }

        if (scroll) {
          scrollThumbIntoView(
            thumbnails[
              activeIndex
            ]
          );
        }
      };

    const replaceMainMedia =
      (
        index,
        {
          animate = true,
          scroll = true
        } = {}
      ) => {
        const source =
          thumbnails[index];

        if (
          !source ||
          !mainGallery
        ) {
          return;
        }

        const sourceMedia =
          source.querySelector(
            '[data-media-id]'
          ) || source;

        const currentMedia =
          mainGallery.querySelector(
            '[data-media-id]'
          );

        const sourceId =
          getMediaId(
            sourceMedia
          );

        if (
          currentMedia &&
          sourceId &&
          getMediaId(
            currentMedia
          ) === sourceId
        ) {
          setActiveState(
            index,
            scroll
          );

          return;
        }

        const clone =
          source.cloneNode(
            true
          );

        clone.classList.remove(
          'ric-gallery__thumb',
          'is-active'
        );

        clone.classList.add(
          'product__media-item'
        );

        clone.removeAttribute(
          'aria-current'
        );

        clone.removeAttribute(
          'role'
        );

        clone.style.width =
          '100%';

        if (animate) {
          animateGallery();
        }

        mainGallery.replaceChildren(
          clone
        );

        setActiveState(
          index,
          scroll
        );

        window.setTimeout(
          () => {
            const modalOpener =
              clone.querySelector(
                'modal-opener'
              );

            if (
              modalOpener &&
              window.Shopify &&
              window.Shopify.designMode
            ) {
              modalOpener.setAttribute(
                'data-ric-gallery-active',
                'true'
              );
            }
          },
          0
        );
      };

    const selectIndex =
      (index) => {
        replaceMainMedia(
          index,
          {
            animate: true,
            scroll: true
          }
        );
      };

    /* --------------------------------------------------------
       THUMBNAIL INTERACTION
       -------------------------------------------------------- */
    thumbnails.forEach(
      (
        thumbnail,
        index
      ) => {
        thumbnail.setAttribute(
          'role',
          'button'
        );

        thumbnail.setAttribute(
          'tabindex',
          '0'
        );

        thumbnail.setAttribute(
          'aria-label',
          `View product media ${index + 1}`
        );

        const activate =
          (event) => {
            event.preventDefault();
            event.stopPropagation();

            selectIndex(
              index
            );
          };

        thumbnail.addEventListener(
          'click',
          activate
        );

        thumbnail.addEventListener(
          'keydown',
          (event) => {
            if (
              event.key === 'Enter' ||
              event.key === ' '
            ) {
              activate(
                event
              );
            }
          }
        );
      }
    );

    /* --------------------------------------------------------
       PREVIOUS BUTTON
       -------------------------------------------------------- */
    previousButton?.addEventListener(
      'click',
      (event) => {
        event.preventDefault();

        if (
          thumbnails.length < 2
        ) {
          return;
        }

        selectIndex(
          activeIndex === 0
            ? thumbnails.length - 1
            : activeIndex - 1
        );
      }
    );

    /* --------------------------------------------------------
       NEXT BUTTON
       -------------------------------------------------------- */
    nextButton?.addEventListener(
      'click',
      (event) => {
        event.preventDefault();

        if (
          thumbnails.length < 2
        ) {
          return;
        }

        selectIndex(
          activeIndex ===
            thumbnails.length - 1
            ? 0
            : activeIndex + 1
        );
      }
    );

    /* --------------------------------------------------------
       KEYBOARD GALLERY NAVIGATION
       -------------------------------------------------------- */
    productPage.addEventListener(
      'keydown',
      (event) => {
        const target =
          event.target;

        if (
          !target.closest(
            '[data-ric-gallery-main], [data-ric-gallery-thumbs]'
          )
        ) {
          return;
        }

        if (
          event.key ===
          'ArrowLeft'
        ) {
          selectIndex(
            activeIndex === 0
              ? thumbnails.length - 1
              : activeIndex - 1
          );
        }

        if (
          event.key ===
          'ArrowRight'
        ) {
          selectIndex(
            activeIndex ===
              thumbnails.length - 1
              ? 0
              : activeIndex + 1
          );
        }
      }
    );

    setActiveState(
      activeIndex,
      false
    );

    /* --------------------------------------------------------
       VARIANT → GALLERY SYNC
       -------------------------------------------------------- */
    const syncGalleryToMainMedia =
      () => {
        const selectedMedia =
          mainGallery?.querySelector(
            '[data-media-id]'
          );

        if (
          !selectedMedia
        ) {
          return;
        }

        const mediaId =
          getMediaId(
            selectedMedia
          );

        const index =
          thumbnails.findIndex(
            (thumb) =>
              getMediaId(
                thumb
              ) === mediaId
          );

        if (
          index >= 0
        ) {
          setActiveState(
            index,
            true
          );
        }
      };

    const variantPicker =
      productPage.querySelector(
        'variant-radios, variant-selects'
      );

    /*
     * Re-run the variant card enhancement whenever the theme
     * updates the variant UI.
     */
    variantPicker?.addEventListener(
      'change',
      () => {
        window.setTimeout(
          enhanceVariantColorOptions,
          0
        );
      }
    );

    variantPicker?.addEventListener(
      'variantChange',
      () => {
        window.setTimeout(
          enhanceVariantColorOptions,
          0
        );
      }
    );

    const variantJson =
      variantPicker?.querySelector(
        'script[type="application/json"]'
      );

    let productVariants =
      [];

    try {
      productVariants =
        variantJson
          ? JSON.parse(
              variantJson.textContent
            )
          : [];
    } catch (error) {
      productVariants =
        [];

      console.warn(
        '[RIC] Unable to read product variant data.',
        error
      );
    }

    /* --------------------------------------------------------
       GET CURRENT SELECTED OPTIONS
       -------------------------------------------------------- */
    const getSelectedOptions =
      () => {
        if (
          !variantPicker
        ) {
          return [];
        }

        if (
          variantPicker.matches(
            'variant-radios'
          )
        ) {
          return Array.from(
            variantPicker.querySelectorAll(
              'fieldset'
            )
          ).map(
            (fieldset) => {
              return (
                fieldset.querySelector(
                  'input[type="radio"]:checked'
                )?.value || ''
              );
            }
          );
        }

        return Array.from(
          variantPicker.querySelectorAll(
            'select'
          )
        ).map(
          (select) =>
            select.value || ''
        );
      };

    /* --------------------------------------------------------
       FIND SELECTED VARIANT
       -------------------------------------------------------- */
    const findSelectedVariant =
      () => {
        const selectedOptions =
          getSelectedOptions();

        if (
          !selectedOptions.length ||
          !productVariants.length
        ) {
          return null;
        }

        return (
          productVariants.find(
            (variant) => {
              return (
                Array.isArray(
                  variant.options
                ) &&
                variant.options.every(
                  (
                    value,
                    index
                  ) => {
                    return (
                      String(
                        value
                      ) ===
                      String(
                        selectedOptions[
                          index
                        ] || ''
                      )
                    );
                  }
                )
              );
            }
          ) || null
        );
      };

    /* --------------------------------------------------------
       MEDIA ID NORMALIZATION
       -------------------------------------------------------- */
    const mediaIdFromData =
      (value) => {
        const match =
          String(
            value || ''
          ).match(
            /-(\d+)$/
          );

        return match
          ? match[1]
          : '';
      };

    /* --------------------------------------------------------
       FIND THUMBNAIL FOR MEDIA
       -------------------------------------------------------- */
    const findThumbnailForMedia =
      (mediaId) => {
        if (
          !mediaId
        ) {
          return null;
        }

        return (
          thumbnails.find(
            (thumbnail) => {
              return (
                mediaIdFromData(
                  getMediaId(
                    thumbnail
                  )
                ) ===
                String(
                  mediaId
                )
              );
            }
          ) || null
        );
      };

    /* --------------------------------------------------------
       FORCE IMAGE LOADING
       -------------------------------------------------------- */
    const ensureImageLoaded =
      (scope) => {
        scope
          ?.querySelectorAll(
            'img'
          )
          .forEach(
            (img) => {
              const lazySource =
                img.getAttribute(
                  'data-src'
                );

              if (
                lazySource &&
                (
                  !img.getAttribute(
                    'src'
                  ) ||
                  img
                    .getAttribute(
                      'src'
                    )
                    .includes(
                      'data:image'
                    )
                )
              ) {
                img.setAttribute(
                  'src',
                  lazySource
                );
              }

              img.removeAttribute(
                'loading'
              );

              img.setAttribute(
                'decoding',
                'async'
              );
            }
          );
      };

    /* --------------------------------------------------------
       PROMOTE VARIANT MEDIA TO MAIN GALLERY
       -------------------------------------------------------- */
    const promoteVariantMedia =
      (
        variant,
        {
          animate = true
        } = {}
      ) => {
        if (
          !mainGallery ||
          !variant?.featured_media?.id
        ) {
          return false;
        }

        const mediaId =
          String(
            variant.featured_media.id
          );

        const thumbnail =
          findThumbnailForMedia(
            mediaId
          );

        if (
          !thumbnail
        ) {
          return false;
        }

        const index =
          thumbnails.indexOf(
            thumbnail
          );

        const currentMedia =
          mainGallery.querySelector(
            '[data-media-id]'
          );

        if (
          currentMedia &&
          mediaIdFromData(
            getMediaId(
              currentMedia
            )
          ) === mediaId
        ) {
          setActiveState(
            index,
            true
          );

          return true;
        }

        const clone =
          thumbnail.cloneNode(
            true
          );

        clone.classList.remove(
          'ric-gallery__thumb',
          'is-active'
        );

        clone.classList.add(
          'product__media-item',
          'ric-variant-media-active'
        );

        clone.removeAttribute(
          'aria-current'
        );

        clone.removeAttribute(
          'role'
        );

        clone.removeAttribute(
          'tabindex'
        );

        clone.style.width =
          '100%';

        clone.style.minWidth =
          '0';

        clone.style.flex =
          '0 0 100%';

        ensureImageLoaded(
          clone
        );

        if (
          animate
        ) {
          animateGallery();
        }

        mainGallery.replaceChildren(
          clone
        );

        setActiveState(
          index,
          true
        );

        window.setTimeout(
          () => {
            ensureImageLoaded(
              clone
            );

            if (
              window.matchMedia(
                '(min-width: 991px)'
              ).matches &&
              window.jQuery &&
              window.jQuery.fn &&
              window.jQuery.fn.elevateZoom
            ) {
              try {
                window.jQuery(
                  clone
                )
                  .find(
                    'img'
                  )
                  .elevateZoom({
                    zoomType:
                      'inner',
                    cursor:
                      'crosshair'
                  });
              } catch (
                error
              ) {
                /*
                 * Zoom is optional.
                 * Variant switching must
                 * continue normally.
                 */
              }
            }
          },
          30
        );

        return true;
      };

    /* --------------------------------------------------------
       SYNC SELECTED VARIANT
       -------------------------------------------------------- */
    const syncGalleryToSelectedVariant =
      ({
        animate = true
      } = {}) => {
        const variant =
          findSelectedVariant();

        if (
          !variant
        ) {
          return false;
        }

        return promoteVariantMedia(
          variant,
          {
            animate
          }
        );
      };

    /* --------------------------------------------------------
       VARIANT CHANGE QUEUE
       -------------------------------------------------------- */
    let variantSyncTimer =
      null;

    let variantSyncSequence =
      0;

    let variantSyncPending =
      false;

    const queueVariantGallerySync =
      () => {
        variantSyncPending =
          true;

        variantSyncSequence +=
          1;

        const sequence =
          variantSyncSequence;

        if (
          variantSyncTimer
        ) {
          window.clearTimeout(
            variantSyncTimer
          );
        }

        variantSyncTimer =
          window.setTimeout(
            () => {
              if (
                sequence !==
                variantSyncSequence
              ) {
                return;
              }

              syncGalleryToSelectedVariant({
                animate: true
              });

              window.setTimeout(
                () => {
                  if (
                    sequence ===
                    variantSyncSequence
                  ) {
                    syncGalleryToSelectedVariant({
                      animate: false
                    });
                  }
                },
                120
              );

              window.setTimeout(
                () => {
                  if (
                    sequence ===
                    variantSyncSequence
                  ) {
                    variantSyncPending =
                      false;
                  }
                },
                650
              );
            },
            0
          );
      };

    /* --------------------------------------------------------
       VARIANT EVENTS
       -------------------------------------------------------- */
    variantPicker?.addEventListener(
      'change',
      queueVariantGallerySync
    );

    variantPicker?.addEventListener(
      'variantChange',
      queueVariantGallerySync
    );

    /* --------------------------------------------------------
       GALLERY MUTATION OBSERVER
       -------------------------------------------------------- */
    if (
      mainGallery &&
      'MutationObserver' in window
    ) {
      const galleryObserver =
        new MutationObserver(
          () => {
            if (
              !variantSyncPending
            ) {
              return;
            }

            window.requestAnimationFrame(
              () => {
                if (
                  !variantSyncPending
                ) {
                  return;
                }

                const variant =
                  findSelectedVariant();

                if (
                  !variant?.featured_media?.id
                ) {
                  return;
                }

                const current =
                  mainGallery.querySelector(
                    '[data-media-id]'
                  );

                const currentId =
                  mediaIdFromData(
                    getMediaId(
                      current
                    )
                  );

                if (
                  currentId !==
                  String(
                    variant.featured_media.id
                  )
                ) {
                  promoteVariantMedia(
                    variant,
                    {
                      animate: false
                    }
                  );
                } else {
                  syncGalleryToMainMedia();
                }
              }
            );
          }
        );

      galleryObserver.observe(
        mainGallery,
        {
          childList: true,
          subtree: false
        }
      );
    }

    /* --------------------------------------------------------
       INITIAL VARIANT IMAGE
       -------------------------------------------------------- */
    window.setTimeout(
      () => {
        syncGalleryToSelectedVariant({
          animate: false
        });
      },
      0
    );

    /* --------------------------------------------------------
       PRICE ANIMATION
       -------------------------------------------------------- */
    const priceElement =
      productPage.querySelector(
        '.ric-price-value'
      );

    variantPicker?.addEventListener(
      'change',
      () => {
        if (
          !priceElement ||
          reduceMotion
        ) {
          return;
        }

        priceElement.classList.remove(
          'ric-price-changing'
        );

        void priceElement.offsetWidth;

        priceElement.classList.add(
          'ric-price-changing'
        );

        window.setTimeout(
          () => {
            priceElement.classList.remove(
              'ric-price-changing'
            );
          },
          320
        );
      }
    );

    /* --------------------------------------------------------
       MOBILE STICKY PURCHASE BAR
       -------------------------------------------------------- */
    const purchaseSection =
      productPage.querySelector(
        '[data-ric-purchase]'
      );

    const mobilePurchase =
      productPage.querySelector(
        '[data-ric-mobile-purchase]'
      );

    const mobileAdd =
      productPage.querySelector(
        '[data-ric-mobile-add]'
      );

    const mobilePrice =
      productPage.querySelector(
        '[data-ric-mobile-price]'
      );

    const desktopPrice =
      productPage.querySelector(
        '.ric-price-value'
      );

    const syncMobilePrice =
      () => {
        if (
          !mobilePrice ||
          !desktopPrice
        ) {
          return;
        }

        mobilePrice.innerHTML =
          desktopPrice.innerHTML;
      };

    syncMobilePrice();

    variantPicker?.addEventListener(
      'change',
      () => {
        window.setTimeout(
          syncMobilePrice,
          120
        );
      }
    );

    const setMobileVisibility =
      (visible) => {
        if (
          !mobilePurchase
        ) {
          return;
        }

        const mobile =
          window.innerWidth <=
          767;

        const show =
          mobile &&
          visible;

        mobilePurchase.classList.toggle(
          'is-visible',
          show
        );

        mobilePurchase.setAttribute(
          'aria-hidden',
          String(
            !show
          )
        );
      };

    if (
      purchaseSection &&
      mobilePurchase &&
      'IntersectionObserver' in window
    ) {
      const purchaseObserver =
        new IntersectionObserver(
          ([entry]) => {
            setMobileVisibility(
              !entry.isIntersecting
            );
          },
          {
            threshold: 0.05
          }
        );

      purchaseObserver.observe(
        purchaseSection
      );

      window.addEventListener(
        'resize',
        () => {
          const rect =
            purchaseSection.getBoundingClientRect();

          setMobileVisibility(
            rect.top >
              window.innerHeight ||
            rect.bottom <
              0
          );
        },
        {
          passive: true
        }
      );
    }

    /* --------------------------------------------------------
       MOBILE ADD TO CART
       -------------------------------------------------------- */
    mobileAdd?.addEventListener(
      'click',
      (event) => {
        event.preventDefault();

        const primaryButton =
          productPage.querySelector(
            '.ric-add-to-cart'
          );

        if (
          !primaryButton
        ) {
          return;
        }

        primaryButton.click();
      }
    );

    /* --------------------------------------------------------
       ADD TO CART FEEDBACK
       -------------------------------------------------------- */
    const addButton =
      productPage.querySelector(
        '.ric-add-to-cart'
      );

    const form =
      addButton?.closest(
        'product-form'
      );

    if (
      addButton &&
      form
    ) {
      form.addEventListener(
        'submit',
        () => {
          addButton.classList.add(
            'loading'
          );

          window.setTimeout(
            () => {
              addButton.classList.remove(
                'loading'
              );
            },
            1500
          );
        }
      );
    }

    /* --------------------------------------------------------
       MOBILE SWIPE GALLERY
       -------------------------------------------------------- */
    if (
      mainGallery &&
      window.innerWidth <=
        767 &&
      thumbnails.length > 1 &&
      !reduceMotion
    ) {
      let startX =
        0;

      let startY =
        0;

      mainGallery.addEventListener(
        'touchstart',
        (event) => {
          const touch =
            event.changedTouches[0];

          startX =
            touch.clientX;

          startY =
            touch.clientY;
        },
        {
          passive: true
        }
      );

      mainGallery.addEventListener(
        'touchend',
        (event) => {
          const touch =
            event.changedTouches[0];

          const dx =
            touch.clientX -
            startX;

          const dy =
            touch.clientY -
            startY;

          /*
           * Ignore vertical swipes.
           */
          if (
            Math.abs(dx) <
              45 ||
            Math.abs(dx) <
              Math.abs(dy)
          ) {
            return;
          }

          /*
           * Swipe left → next.
           */
          if (
            dx < 0
          ) {
            selectIndex(
              activeIndex ===
                thumbnails.length - 1
                ? 0
                : activeIndex + 1
            );
          }

          /*
           * Swipe right → previous.
           */
          else {
            selectIndex(
              activeIndex === 0
                ? thumbnails.length - 1
                : activeIndex - 1
            );
          }
        },
        {
          passive: true
        }
      );
    }
  };

  /* ----------------------------------------------------------
     INITIALIZE
     ---------------------------------------------------------- */
  if (
    document.readyState ===
    'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      init,
      {
        once: true
      }
    );
  } else {
    init();
  }
})();